import { Component, OnInit } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
  ConsentAlertsService,
  AlertsDashboard,
  AlertSummary,
  AlertHistory,
  PendingAlert,
} from '../../services/consent-alerts/consent-alerts.service';
import { ModalService } from '../../services/modal/modal.service';
import { SettingsService } from '../../services/setting-service/setting.service';
@Component({
  selector: 'app-consent-alerts',
  standalone: false,
  templateUrl: './consent-alerts.html',
  styleUrl: './consent-alerts.scss',
})
export class ConsentAlertsPage implements OnInit {
  data: AlertsDashboard | null = null;
  busy = false;
  error = '';
  result: AlertSummary | null = null;
  previewResult = false;
  form = { enabled: false };
  verified: Record<string, boolean> = {};
  private dashboardIsCurrent: (() => boolean) | null = null;
  constructor(
    public api: ConsentAlertsService,
    private modal: ModalService,
    private settings: SettingsService,
  ) {}
  ngOnInit() {
    this.settings.title = 'Alertas de termos';
    void this.load();
  }
  private captureContext() {
    const owner = this.settings.user?.token;
    const apiUrl = this.settings.configUso?.urlApiPadrao;
    const ambiente = this.api.ambiente;
    return () =>
      owner === this.settings.user?.token &&
      apiUrl === this.settings.configUso?.urlApiPadrao &&
      ambiente === this.api.ambiente;
  }
  private requireCurrentDashboard() {
    if (!this.data || !this.dashboardIsCurrent?.())
      throw Error('A sessão ou a API mudou. Atualize o painel antes de continuar.');
  }
  private invalidateDashboard() {
    this.data = null;
    this.dashboardIsCurrent = null;
    this.verified = {};
    this.result = null;
  }
  private apply(data: AlertsDashboard) {
    this.invalidateDashboard();
    if (!this.api.ambiente || (data.ambiente !== undefined && data.ambiente !== this.api.ambiente))
      throw Error('Ambiente dos alertas não confirmado. Recarregue antes de enviar.');
    this.data = { ...data, ambiente: this.api.ambiente };
    this.dashboardIsCurrent = this.captureContext();
    this.form = {
      enabled: data.enabled,
    };
  }
  async load() {
    if (this.busy) return;
    this.invalidateDashboard();
    this.busy = true;
    this.error = '';
    try {
      this.apply(await this.api.get());
    } catch (e) {
      this.failure(e);
    } finally {
      this.busy = false;
    }
  }
  async save() {
    if (this.busy) return;
    this.busy = true;
    this.error = '';
    try {
      this.requireCurrentDashboard();
      this.apply(await this.api.configure(this.form));
      this.modal.success('Configuração salva.');
    } catch (e) {
      this.failure(e);
    } finally {
      this.busy = false;
    }
  }
  async simulate() {
    if (this.busy) return;
    this.busy = true;
    this.error = '';
    try {
      this.requireCurrentDashboard();
      this.result = await this.api.preview();
      this.previewResult = true;
    } catch (e) {
      this.failure(e);
    } finally {
      this.busy = false;
    }
  }
  async execute() {
    if (this.busy) return;
    const ambiente = this.api.ambiente;
    const sameContext = this.captureContext();
    let executedResult: AlertSummary | null = null;
    this.busy = true;
    this.error = '';
    try {
      this.requireCurrentDashboard();
      if (!ambiente || !this.data || this.data.ambiente !== ambiente)
        throw Error('Ambiente dos alertas não confirmado. Recarregue antes de enviar.');
      const confirmed = await this.confirm(
        'Enviar alertas agora?',
        ambiente === 'prod'
          ? 'Serão enviados os avisos devidos aos participantes elegíveis. Avisos já enviados não serão repetidos.'
          : 'Serão enviados somente os avisos devidos às três contas de teste de DEV. Avisos já enviados não serão repetidos.',
        'Enviar alertas',
      );
      if (!sameContext())
        throw Error('A sessão ou a API mudou. Atualize o painel antes de continuar.');
      if (!confirmed) return;
      this.result = null;
      executedResult = await this.api.run(ambiente);
      if (!sameContext()) throw Error('A sessão ou o ambiente mudou. Recarregue o painel.');
      const dashboard = await this.api.get();
      if (!sameContext()) throw Error('A sessão ou o ambiente mudou. Recarregue o painel.');
      this.apply(dashboard);
      this.result = executedResult;
      this.previewResult = false;
    } catch (e) {
      this.failure(e);
      if (executedResult && sameContext()) {
        this.result = executedResult;
        this.previewResult = false;
        this.error =
          'O resultado da solicitação foi confirmado, mas não foi possível atualizar o painel. O resultado confirmado está abaixo. Atualize o painel antes de realizar outra ação.';
      }
    } finally {
      this.busy = false;
    }
  }
  key(row: PendingAlert) {
    return `${row.id_consentimento}-${row.faixa}`;
  }
  async resolve(row: PendingAlert, delivered: boolean) {
    if (this.busy || !this.data || !this.verified[this.key(row)]) return;
    const sameContext = this.captureContext();
    this.busy = true;
    this.error = '';
    try {
      this.requireCurrentDashboard();
      const message = delivered
        ? 'Este aviso será marcado como enviado e não será repetido.'
        : 'A próxima execução poderá reenviar o aviso, se o termo continuar elegível. Confirme apenas após verificar que o provedor NÃO aceitou o envio anterior.';
      const confirmed = await this.confirm(
        'Confirmar revisão do envio?',
        message,
        'Confirmar revisão',
      );
      if (!sameContext())
        throw Error('A sessão ou a API mudou. Atualize o painel antes de continuar.');
      if (!confirmed) return;
      this.apply(
        await this.api.resolve(row, delivered ? 'confirmar_envio' : 'confirmar_nao_envio'),
      );
      this.verified = {};
    } catch (e) {
      this.failure(e);
    } finally {
      this.busy = false;
    }
  }
  status(value: string) {
    return (
      (
        {
          sending: 'Entrega não confirmada',
          uncertain: 'Entrega não confirmada',
          failed: 'Falha de envio',
          retry: 'Nova tentativa agendada',
          sent: 'Enviado',
          cancelled: 'Cancelado: termo ou configuração alterados',
        } as Record<string, string>
      )[value] || value
    );
  }
  historyDetail(row: AlertHistory) {
    if (row.tipo === 'CONSENTIMENTO_CONFIG') return 'Configuração do agendador atualizada.';
    if (!Array.isArray(row.destinatarios)) return 'Registro indisponível.';
    return row.destinatarios
      .map(
        (r: { email?: string; status?: string }) =>
          `${r.email || 'Destinatário'} — ${this.status(r.status || '')}`,
      )
      .join('; ');
  }
  reason(value?: string) {
    return (
      (
        {
          disabled: 'Agendador pausado.',
          already_running: 'Já existe uma execução em andamento.',
          not_due: 'Aguardando o próximo horário.',
        } as Record<string, string>
      )[value || ''] || ''
    );
  }
  private async confirm(title: string, message: string, label: string) {
    let confirmed = false;
    const ref = this.modal.show({
      type: 'warning',
      title,
      message,
      listButtons: [
        { textButton: 'Voltar', fill: 'outline' },
        {
          textButton: label,
          fill: 'solid',
          action: () => {
            confirmed = true;
          },
        },
      ],
    });
    await lastValueFrom(ref.afterClosed());
    return confirmed;
  }
  private failure(error: unknown) {
    this.invalidateDashboard();
    const e = error as { status?: number; error?: { message?: string }; message?: string };
    this.error =
      e.status === 0
        ? 'Não foi possível conectar ao serviço. Tente novamente.'
        : e.error?.message || e.message || 'Não foi possível concluir a operação.';
  }
}
